import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useDisputeClaim } from '@/hooks/usePetPlaces';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeId: string | null;
}

export default function DisputeClaimDialog({ open, onOpenChange, placeId }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [contact, setContact] = useState('');
  const { mutateAsync, isPending } = useDisputeClaim();

  useEffect(() => {
    if (open) {
      setReason('');
      setContact('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!placeId) return;
    if (reason.trim().length < 5) {
      toast({
        title: t('places.dispute.reasonRequired', 'Please describe the issue (min 5 chars)'),
        variant: 'destructive',
      });
      return;
    }
    try {
      await mutateAsync({
        placeId,
        reason: reason.trim(),
        contact: contact.trim() ? { contact: contact.trim() } : undefined,
      });
      toast({
        title: t('places.dispute.success', 'Dispute submitted'),
        description: t('places.dispute.successDesc', "Our team will review it."),
      });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: t('common.error', 'Error'), description: e.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('places.report.title', 'Report a problem')}</DialogTitle>
          <DialogDescription>
            {t('places.report.desc', 'Tell us what is wrong: incorrect info, duplicate, closed, or wrong owner.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('places.dispute.reason', 'Reason')} *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder={t('places.report.reasonPlaceholder', 'e.g. Duplicate of "Cafe X", address is wrong, place is permanently closed...') as string}
            />
          </div>
          <div>
            <Label>{t('places.dispute.contact', 'Your contact (optional)')}</Label>
            <Input value={contact} onChange={(e) => setContact(e.target.value)} maxLength={120} />
          </div>
          <Button className="w-full" onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('places.dispute.submit', 'Submit dispute')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
